import { useRef, useCallback } from 'react';
import format from 'string-template';

import useToast from '../../../../hooks/useToast';
import { filenameToTitle } from '../../../../../utils/string';
import { UploadStatus, SaveStatus } from './constants';
import {
  createCard,
  buildUpload,
  isVideoFile,
  oversizeError,
} from './utils';
import texts from './texts';

/**
 * File intake for the bulk creator: turns dropped/selected videos into cards
 * (auto-enqueueing their uploads) and owns the hidden "Add a video" picker
 * used by individual cards. The parent renders the file input with the
 * returned ref/handler and keeps ownership of the card state.
 */
const useCardFiles = ({
  cardsRef,
  setCards,
  updateCard,
  enqueue,
  cancel,
  cleanUpVimeoVideo,
}) => {
  const { showToast } = useToast();

  const fileInputRef = useRef(null);
  const pendingCardIdRef = useRef(null); // card awaiting an attached video via the picker

  /* ---------- per-card attach (picker + session-recovery re-drop) ---------- */
  const attachVideoToCard = useCallback((cardId, file) => {
    const error = oversizeError(file);
    const previous = cardsRef.current.find((item) => item.id === cardId);

    // Stop any in-flight upload for this card and clean up a video that had
    // already reached Vimeo before being replaced.
    cancel(cardId);
    if (previous && previous.upload && previous.upload.videoId) {
      cleanUpVimeoVideo(previous.upload.videoId);
    }

    updateCard(cardId, (card) => {
      if (card.previewUrl) {
        URL.revokeObjectURL(card.previewUrl);
      }
      // Refresh the auto-filled name from the new file, unless the coach has
      // customised it (i.e. it no longer matches the previous file's derived name).
      const previousAutoName = card.file ? filenameToTitle(card.file.name) : '';
      const name = !card.name.trim() || card.name === previousAutoName
        ? filenameToTitle(file.name)
        : card.name;
      return {
        ...card,
        file,
        name,
        fileName: file.name,
        previewUrl: URL.createObjectURL(file),
        upload: buildUpload(file, error),
      };
    });
    if (!error) {
      enqueue(cardId, file);
    }
  }, [cardsRef, cancel, cleanUpVimeoVideo, updateCard, enqueue]);

  // A restored card whose upload never finished lost its File with the page —
  // it needs the same file re-selected before its upload can be retried.
  const findCardAwaitingFile = useCallback((file, claimedIds) => cardsRef.current.find((card) => (
    !claimedIds.has(card.id)
    && !card.file
    && card.fileName === file.name
    && card.save !== SaveStatus.SAVED
    && card.upload && card.upload.status === UploadStatus.FAILED
  )), [cardsRef]);

  /* ---------- drop / multi-select → append one card each ---------- */
  const appendCards = useCallback((fileList) => {
    const files = Array.from(fileList);
    const videoFiles = files.filter(isVideoFile);
    const nonVideoCount = files.length - videoFiles.length;

    if (videoFiles.length) {
      // Session recovery: files matching a restored card's filename re-attach
      // to that card (resuming its upload) instead of creating a duplicate.
      const claimedIds = new Set();
      const freshFiles = [];
      videoFiles.forEach((file) => {
        const match = findCardAwaitingFile(file, claimedIds);
        if (match) {
          claimedIds.add(match.id);
          attachVideoToCard(match.id, file);
        } else {
          freshFiles.push(file);
        }
      });

      const newCards = freshFiles.map((file) => createCard({
        file,
        name: filenameToTitle(file.name),
        uploadError: oversizeError(file),
      }));
      if (newCards.length) {
        setCards((previous) => [...previous, ...newCards]);
        // Valid files enter the upload queue immediately (auto-upload on drop);
        // oversized ones were created in a failed state and are not enqueued.
        newCards.forEach((card) => {
          if (card.upload.status === UploadStatus.QUEUED) {
            enqueue(card.id, card.file);
          }
        });
      }
      showToast(format(texts.toastVideosAdded, { count: videoFiles.length }));
    }
    if (nonVideoCount > 0) {
      showToast(format(texts.toastInvalidFiles, { count: nonVideoCount }), { warning: true });
    }
  }, [findCardAwaitingFile, attachVideoToCard, setCards, showToast, enqueue]);

  const handleAddVideo = (cardId) => {
    pendingCardIdRef.current = cardId;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (event) => {
    const { files } = event.target;
    const pendingId = pendingCardIdRef.current;
    if (files && files.length && pendingId) {
      const [file] = Array.from(files).filter(isVideoFile);
      if (file) {
        attachVideoToCard(pendingId, file);
      }
    }
    pendingCardIdRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    fileInputRef,
    appendCards,
    handleAddVideo,
    handleFileInputChange,
  };
};

export default useCardFiles;
